'use client';

import Link from 'next/link';
import { ArrowDown, ArrowUpRight } from '@phosphor-icons/react';
import type { PublicPromotion } from '@/lib/promotion-content';
import './intro-options.css';

export const introOptions = [
  {
    id: 'compact',
    name: 'Компактный',
    note: 'Короткий заголовок. Внимание сразу на предложениях.',
  },
  {
    id: 'editorial',
    name: 'Типографика',
    note: 'Контраст двух шрифтов и спокойная журнальная композиция.',
  },
  { id: 'navigation', name: 'Навигация', note: 'Условия нужной акции можно открыть прямо отсюда.' },
  { id: 'mascot', name: 'С персонажем', note: 'Невысокий брендовый блок с вашим персонажем.' },
] as const;

export type IntroOption = (typeof introOptions)[number]['id'];

export function IntroOptionsToolbar({
  value,
  onChange,
}: {
  value: IntroOption;
  onChange: (value: IntroOption) => void;
}) {
  return (
    <aside className="intro-options-toolbar" aria-label="Выбор оформления верхнего блока">
      <div className="intro-options-toolbar-top">
        <span>Варианты верхнего блока</span>
        <Link href="/actions">
          Вернуться к акциям <ArrowUpRight size={14} aria-hidden="true" />
        </Link>
      </div>
      <div className="intro-options-tabs" role="tablist" aria-label="Вариант дизайна">
        {introOptions.map((option, index) => (
          <button
            key={option.id}
            type="button"
            role="tab"
            id={`intro-tab-${option.id}`}
            aria-selected={value === option.id}
            aria-controls="intro-option-panel"
            tabIndex={value === option.id ? 0 : -1}
            onKeyDown={(event) => {
              const next =
                event.key === 'ArrowRight'
                  ? (index + 1) % introOptions.length
                  : event.key === 'ArrowLeft'
                    ? (index + introOptions.length - 1) % introOptions.length
                    : event.key === 'Home'
                      ? 0
                      : event.key === 'End'
                        ? introOptions.length - 1
                        : null;
              if (next === null) return;
              event.preventDefault();
              onChange(introOptions[next].id);
              document.getElementById(`intro-tab-${introOptions[next].id}`)?.focus();
            }}
            onClick={() => onChange(option.id)}
          >
            <span>{String(index + 1).padStart(2, '0')}</span>
            {option.name}
          </button>
        ))}
      </div>
      <p>{introOptions.find((option) => option.id === value)?.note}</p>
    </aside>
  );
}

const navigationLabels: Record<string, string> = {
  firstOrder: 'Первый заказ',
  statusRolls: 'Статусные роллы',
  review: 'Подарок за отзыв',
  loyalty: 'Кэшбэк коинами',
  birthday: 'День рождения',
};

function offerWord(count: number) {
  const last = count % 10;
  return count % 100 >= 11 && count % 100 <= 14
    ? 'предложений'
    : last === 1
      ? 'предложение'
      : last >= 2 && last <= 4
        ? 'предложения'
        : 'предложений';
}

export function CompactOffersIntro({ count }: { count: number }) {
  const number = String(count).padStart(2, '0');
  return (
    <div className="intro-compact-row">
      <div className="intro-compact-title">
        <h1 id="offers-title">Акции</h1>
        <span className="intro-compact-count" aria-label={`${count} ${offerWord(count)}`}>
          {number}
        </span>
      </div>
      <a className="intro-quiet-link" href="#offers-faq">
        Об акциях <ArrowDown size={17} aria-hidden="true" />
      </a>
    </div>
  );
}

export function OfferIntroOption({
  variant,
  offers,
  onOpen,
}: {
  variant: IntroOption;
  offers: PublicPromotion[];
  onOpen: (offer: PublicPromotion, trigger: HTMLButtonElement) => void;
}) {
  const count = offers.length;
  const number = String(count).padStart(2, '0');
  return (
    <section
      className={`intro-option intro-option--${variant}`}
      id="intro-option-panel"
      role="tabpanel"
      aria-labelledby={`intro-tab-${variant}`}
    >
      {variant === 'compact' && <CompactOffersIntro count={count} />}
      {variant === 'editorial' && (
        <div className="intro-editorial-row">
          <div>
            <p className="intro-overline">Азиаточка / предложения</p>
            <h1 id="offers-title">
              Акции <em>и подарки</em>
            </h1>
          </div>
          <a className="intro-editorial-index" href="#offers-faq">
            <span className="intro-editorial-number">{number}</span>
            <span>
              {offerWord(count)}
              <ArrowDown size={18} aria-hidden="true" />
            </span>
          </a>
        </div>
      )}
      {variant === 'navigation' && (
        <>
          <div className="intro-navigation-heading">
            <h1 id="offers-title">Акции</h1>
            <span>Выберите предложение</span>
          </div>
          <nav className="intro-navigation-links" aria-label="Условия акций">
            {offers.map((offer, index) => {
              const label = navigationLabels[offer.detail_key || ''];
              return label ? (
                <button
                  key={offer.id}
                  type="button"
                  aria-haspopup="dialog"
                  onClick={(event) => onOpen(offer, event.currentTarget)}
                >
                  <span className="intro-navigation-number">
                    {String(index + 1).padStart(2, '0')}
                  </span>
                  <span>{label}</span>
                  <ArrowUpRight size={20} aria-hidden="true" />
                </button>
              ) : (
                <span className="intro-navigation-info" key={offer.id}>
                  {offer.title}
                </span>
              );
            })}
          </nav>
        </>
      )}
      {variant === 'mascot' && (
        <div className="intro-mascot-panel">
          <div className="intro-mascot-copy">
            <span className="intro-overline">К вашему заказу</span>
            <h1 id="offers-title">Акции и подарки</h1>
            <a className="intro-quiet-link" href="#offers-faq">
              Условия предложений <ArrowDown size={17} aria-hidden="true" />
            </a>
          </div>
          <img
            src="/storefront/offers-mascot-wave-v1.webp"
            alt=""
            width={1086}
            height={1448}
            className="intro-mascot-image"
          />
        </div>
      )}
    </section>
  );
}
